import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../../assets/images/Logo.png";
import { Language, LANGUAGES } from "../../i18n/i18n";
import { applyLanguage } from "../../i18n/userLanguage";
import { FieldFrame, FieldLayout, fieldPadding } from "./FieldFrame";
import { Select } from "./Select";

const languageOptions: Record<
  Language,
  { name: string; text: string; flag: string }
> = {
  fr: {
    name: "Français",
    text: t("language-fr"),
    flag: "🇫🇷",
  },
  en: {
    name: "English",
    text: t("language-en"),
    flag: "🇬🇧",
  },
  es: {
    name: "Español",
    text: t("language-es"),
    flag: "🇪🇸",
  },
  cn: {
    name: "中文",
    text: t("language-cn"),
    flag: "🇨🇳",
  },
};

type FormLayoutProps = {
  children: React.ReactNode;
  title?: string;
  maxWidth?: string;
};

type FormInputProps = {
  label?: string;
  layout?: FieldLayout;
  required?: boolean;
  sizeInput?: "regular" | "small";
  error?: string;
  /**
   * Champ non modifiable PAR NATURE (donnée qui ne s'édite pas ici), à distinguer de
   * `disabled` qui veut dire « momentanement indisponible ».
   *
   * Utilise `readOnly` et non `disabled` : un champ `disabled` est retiré de l'ordre de
   * tabulation et souvent ignoré par les lecteurs d'ecran — l'information devient
   * invisible au clavier. `readOnly` reste focusable et lisible, mais refuse la saisie.
   * `hint` explique POURQUOI, sinon l'utilisateur croit a un bug.
   */
  lockedHint?: string;
};

type FormSelectProps = {
  label?: string;
  layout?: FieldLayout;
  required?: boolean;
  options: { value: string | number; label: string }[];
  error?: string;
} & Omit<React.ComponentProps<typeof Select>, "children" | "error" | "size">;

type FormCheckboxProps = {
  label: string;
  name: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
};

export const FormLayout = ({
  children,
  title,
  maxWidth = "md",
}: FormLayoutProps) => {
  const [showLanguages, setShowLanguages] = useState(false);
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (lang: Language) => {
    applyLanguage(lang);
    setShowLanguages(false);
  };

  const currentLanguage = languageOptions[i18n.language as Language];

  return (
    <div className="min-h-screen relative bg-cover bg-center bg-login">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <div className="relative">
          <button
            onClick={() => setShowLanguages(!showLanguages)}
            className="bg-white/20 w-10 h-10 rounded-full hover:bg-white/30 transition-colors flex items-center justify-center"
            title={currentLanguage.name}
          >
            <FontAwesomeIcon icon="globe" className="text-white h-5 w-5" />
          </button>

          {showLanguages && (
            <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg overflow-hidden min-w-[160px]">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full px-4 py-2 text-left hover:bg-grey-light flex items-center gap-2
                    ${i18n.language === lang ? "bg-grey-light" : ""}`}
                >
                  <span>{languageOptions[lang].flag}</span>
                  <span className="text-tertiary">
                    {languageOptions[lang].text}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Form Container */}
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <img src={Logo} alt="Copernic" className="h-16 mx-auto" />
        <div className={`bg-secondary rounded-lg p-8 w-full max-w-${maxWidth}`}>
          <div className="text-center mb-8">
            {title && (
              <h1 className="text-2xl font-oxaniumMedium text-tertiary">
                {t(title)}
              </h1>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// Composants réutilisables pour les formulaires
export const FormInput = ({
  label,
  layout = "stacked",
  sizeInput = "regular",
  required,
  error,
  lockedHint,
  ...props
}: FormInputProps & React.InputHTMLAttributes<HTMLInputElement>) => {
  // Un champ verrouille est en lecture seule ET signale visuellement qu'il l'est.
  // Sans le fond grise, l'utilisateur essaie de taper et croit l'appli cassee.
  const locked = Boolean(lockedHint) || props.readOnly;

  // Le <label> n'etait relie a AUCUN champ (ni `for`, ni `id`) : un lecteur d'ecran
  // annoncait « zone de saisie » sans dire laquelle. On retombe sur le `name`, deja
  // unique dans un formulaire, quand aucun `id` n'est fourni.
  const inputId = props.id ?? props.name;

  return (
    <FieldFrame
      layout={layout}
      label={label}
      required={required}
      htmlFor={inputId}
      error={error}
    >
      <input
        {...props}
        id={inputId}
        readOnly={locked || props.readOnly}
        aria-readonly={locked || undefined}
        title={lockedHint ?? props.title}
        className={`w-full rounded-lg border ${
          error ? "border-red-500" : "border-grey-medium"
        } ${
          locked
            ? "bg-grey-light text-grey-readable cursor-default focus:border-grey-medium"
            : "focus:border-tertiary"
        } focus:outline-none disabled:bg-grey-light/60 disabled:text-grey-readable disabled:cursor-not-allowed ${fieldPadding(
          layout,
          sizeInput === "small"
        )}`}
      />
      {lockedHint && !error && (
        <p className="mt-1 text-xs text-grey-readable">{lockedHint}</p>
      )}
    </FieldFrame>
  );
};

export const FormSelect = ({
  label,
  layout = "stacked",
  required,
  options,
  error,
  ...props
}: FormSelectProps) => {
  const { t } = useTranslation();

  return (
    <FieldFrame
      layout={layout}
      label={label}
      required={required}
      htmlFor={props.id ?? props.name}
      error={error}
    >
      <Select
        {...props}
        id={props.id ?? props.name}
        size={layout === "row" ? "row" : "regular"}
        error={Boolean(error)}
      >
        <option value="">{t("common-select")}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.label)}
          </option>
        ))}
      </Select>
    </FieldFrame>
  );
};

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  label,
  name,
  checked,
  onChange,
  required,
  disabled,
  className,
}) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={checked}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="w-4 h-4 text-primary border-grey-medium rounded focus:ring-primary focus:ring-2 cursor-pointer
          disabled:cursor-not-allowed disabled:opacity-50"
      />
      <label
        htmlFor={name}
        className={`block mb-2 cursor-pointer
          ${disabled ? "cursor-not-allowed opacity-50" : "text-tertiary"}`}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
  );
};
